import { type Control, Controller, type FieldErrors, useWatch } from 'react-hook-form';
import { TooltipDottedLineManager } from '@/common/components/employees/shared/TooltipDottedLineManager';
import { EmployeeSelectSingle } from '@/common/components/shared/select/employee/EmployeeSelect';
import { EmployeeStatus, type GetManagerQuery } from '@/common/graphql';
import type { EmployeeGeneralInformationFormDataType } from '@/common/types';
import { ManagerInfo } from './ManagerInfo';

interface Props {
  primaryManager: GetManagerQuery['employee'] | undefined;
  dottedLineManager: GetManagerQuery['employee'] | undefined;
  loading: boolean;
  control: Control<EmployeeGeneralInformationFormDataType>;
  employeeId: string;
  errors: FieldErrors<EmployeeGeneralInformationFormDataType>;
}

export const ManagerFields = ({ primaryManager, dottedLineManager, loading, control, employeeId, errors }: Props) => {
  const primaryManagerId = useWatch({ control, name: 'primaryManagerId' });

  return (
    <>
      <Controller
        control={control}
        name="primaryManagerId"
        render={({ field }) => (
          <div className="col-span-12 sm:col-span-6">
            <EmployeeSelectSingle
              {...field}
              disabled={loading}
              error={errors.primaryManagerId?.message}
              label="Primary Manager"
              placeholder="Primary Manager"
              required={true}
              variables={{
                excludeIds: employeeId ? [employeeId] : [],
                includeIds: field.value ? [field.value] : [],
                statuses: [EmployeeStatus.Active],
              }}
              onChange={option => field.onChange(option?.value ?? null)}
            />
            {primaryManager && <ManagerInfo managerInfo={primaryManager} showLink={true} />}
          </div>
        )}
      />
      <Controller
        control={control}
        name="dottedLineManagerId"
        render={({ field }) => (
          <div className="col-span-12 sm:col-span-6">
            <EmployeeSelectSingle
              {...field}
              disabled={loading}
              error={errors.dottedLineManagerId?.message}
              label={
                <div className="flex items-center gap-x-1">
                  {/* Plain div, not Input.Label: ComboboxWrapper already wraps this whole `label`
                      value in its own <label> element, so nesting Input.Label's <label> inside
                      it here would produce invalid nested <label> HTML. */}
                  <div>Dotted Line Manager</div>
                  <TooltipDottedLineManager />
                </div>
              }
              placeholder="Dotted Line Manager"
              variables={{
                excludeIds: [...(employeeId ? [employeeId] : []), ...(primaryManagerId ? [primaryManagerId] : [])],
                includeIds: field.value ? [field.value] : [],
                statuses: [EmployeeStatus.Active],
              }}
              onChange={option => field.onChange(option?.value ?? null)}
            />
            {dottedLineManager && <ManagerInfo managerInfo={dottedLineManager} showLink={true} />}
          </div>
        )}
      />
    </>
  );
};
